import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BadgeIcon from '@mui/icons-material/Badge'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import SecurityIcon from '@mui/icons-material/Security'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Router from 'next/router'
import React from 'react'
import SpecialistCard from 'src/components/LeadForm/SpecialistCard'
import { useAuthProvider } from 'src/context/AuthProvider'
import useTrackEvent from 'src/hooks/useTrackEvent'
import { useListingVariant } from 'src/lib/listingVariants'
import { RedirectUrls } from 'src/types/types'
import useAddToCart from 'src/utils/pixels/common'
import { getLocationProfile } from 'src/utils/profile'
import { IConfiguration, ILocation } from '../../types/graphql'
import { formatTimestampMonthDay } from '../../utils'

type VerificationItem = {
  Icon: typeof PhoneIcon
  text: string
  verified?: boolean | null
}

/** Hairline verification grid — identical on the named and anonymous hosts. */
function VerificationGrid({ items }: { items: VerificationItem[] }) {
  return (
    <dl className="mt-8 grid grid-cols-1 gap-x-6 gap-y-3 border-t border-ink-100/15 pt-6 md:grid-cols-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2.5">
          <item.Icon
            sx={{ fontSize: 14 }}
            className={item.verified ? 'text-blue-500' : 'text-ink-200'}
          />
          <dt className="sr-only">Verificación</dt>
          <dd
            className={`text-[13px] md:text-sm ${item.verified ? 'text-ink-700' : 'text-ink-200'}`}
          >
            {item.text}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/** Small-caps meta line — measured only (Location.respondsWithin24h); hidden otherwise. */
function ResponseTime({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <div className="mt-5 inline-flex items-center gap-2">
      <AccessTimeIcon sx={{ fontSize: 14 }} className="text-blue-500" />
      <span className="font-display text-[11px] tracking-[0.16em] text-ink-700 uppercase md:text-xs">
        Responde en menos de 24 h
      </span>
    </div>
  )
}

/** Editorial side note with a left rule. */
function PaymentProtectionNote() {
  return (
    <p className="mt-8 flex items-start gap-3 border-l-2 border-blue-500 bg-cream-300 px-5 py-4 text-[13px] leading-relaxed text-ink-700 md:text-sm">
      <SecurityIcon sx={{ fontSize: 16 }} className="mt-0.5 text-blue-500" />
      <span>
        Tus pagos están protegidos al reservar y comunicarte a través de SpotMe.
      </span>
    </p>
  )
}

/**
 * Inline SVG — no <img>, no alt attribute, no hydration drift (see the
 * hydration-safe icon rules in CLAUDE.md). The adjacent text label carries
 * the semantic meaning.
 */
function VerifiedCheck({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M15.75 8C15.75 12.2802 12.2802 15.75 8 15.75C3.71978 15.75 0.25 12.2802 0.25 8C0.25 3.71978 3.71978 0.25 8 0.25C12.2802 0.25 15.75 3.71978 15.75 8ZM7.10356 12.1036L12.8536 6.35356C13.0488 6.15831 13.0488 5.84172 12.8536 5.64647L12.1465 4.93937C11.9512 4.74409 11.6346 4.74409 11.4393 4.93937L6.75 9.62869L4.56066 7.43934C4.36541 7.24409 4.04881 7.24409 3.85353 7.43934L3.14644 8.14644C2.95119 8.34169 2.95119 8.65828 3.14644 8.85353L6.39644 12.1035C6.59172 12.2988 6.90828 12.2988 7.10356 12.1036Z"
        fill="#5BE793"
      />
    </svg>
  )
}

function User({
  location,
  isBrokerMode,
  configuration,
  quantity,
  storageTime
}: {
  location: ILocation
  isBrokerMode: boolean
  configuration: IConfiguration
  quantity?: number
  storageTime?: string
}) {
  const { t } = useTranslation('details')
  const variant = useListingVariant()
  const userVariant = variant.sections.user.variant
  const configurationId = configuration?.id
  const { currentUser, toggleLoginModal } = useAuthProvider()
  const { trackEvent, EventAction } = useTrackEvent()
  const handleAddToCart = useAddToCart()
  const { href, hrefWithLocation, name, pp, desc, user, contact } =
    getLocationProfile(location, configurationId)
  const { createdOn } = user ?? {}
  // 🛑 `location.hostIsVerified`, NOT `user.isVerified`. `Location.user`
  // resolves off `submittedBy`, and on an admin-onboarded team listing that is
  // a SpotMe admin — always verified — so the badge would be asserting OUR
  // verification, not the host's (31 live listings on 2026-09-09; measured by
  // `yarn audit-host-verification` in spotme-server). The server field resolves
  // team-first and excludes staff. Undefined (an older cached client) reads as
  // false: a trust claim must fail closed.
  const isVerified = location.hostIsVerified === true

  // Unit 15: hide the host entirely when the variant says so.
  if (userVariant === 'hidden') {
    return null
  }

  // Unit 15: flip the host UI for the specialist card on Broker/Traditional/
  // Fleet variants. Exposing the legacy host masthead to renters on those
  // tiers is the "leak the landlord" problem — the renter can route around
  // SpotMe directly to the host. SpecialistCard is editorial-width
  // (rendered as a section in Details/index.tsx) so layout="inline" matches
  // the surrounding rail.
  //
  // No own `border-t` or large top spacing here. Details/index.tsx renders
  // a `<SectionDivider />` above this section unconditionally, which owns
  // the visual break. The specialist branch used to add its own hairline
  // + `pt-8` on top of the parent divider, producing two visible lines.
  if (userVariant === 'specialist') {
    // Traditional B2C self-storage gets the softer "concierge" framing —
    // calling Fernando an "asesor" overstates the friction for a renter
    // who just wants a mini bodega. Broker + Fleet keep the specialist
    // tone since the deal genuinely starts as a scope conversation.
    const isTraditional = variant.meta.dealType === 'TRADITIONAL'
    const cardTone = isTraditional ? 'concierge' : 'specialist'
    const handleSpecialistWaClick = () => {
      // ADD_TO_CART pixel + ES event for the specialist's WA shortcut.
      // `subType: 'contact_wa'` matches the floating WA FAB; backend
      // resolves dealType + locationType server-side from configurationId
      // (via `resolveConfigParams`) so GA4 / Meta / UET / LinkedIn see
      // the right segmentation per listing.
      handleAddToCart(
        'contact_wa',
        location,
        configuration,
        quantity,
        // `storageTime` arrives as a plain `string | undefined` URL param;
        // useAddToCart expects the `StorageTime | ''` enum union. Cast
        // here — invalid values are tolerated downstream (the helper only
        // forwards into pixel params, no enum-shape validation).
        storageTime as never
      )
    }
    return (
      <section aria-labelledby="specialist-section-heading">
        <h2 id="specialist-section-heading" className="sr-only">
          {isTraditional
            ? t('SPECIALIST.CONCIERGE.EYEBROW')
            : t('SPECIALIST.EYEBROW')}
        </h2>
        <SpecialistCard
          specialistKey={variant.leadForm.specialistKey ?? 'fernando'}
          showResponseBadge={variant.leadForm.responseBadge.show}
          layout="inline"
          location={location}
          configuration={configuration}
          contextValues={{
            when: storageTime ?? null,
            quantity: quantity ?? null
          }}
          tone={cardTone}
          onWhatsappClick={handleSpecialistWaClick}
        />
      </section>
    )
  }

  const handleContactHost = () => {
    handleAddToCart('chat_host', location, configuration)
    trackEvent({
      action: EventAction.Contact,
      locationId: location.id,
      configurationId: configuration?.id
    })
    if (!currentUser) {
      toggleLoginModal(true, 'chat_host')
      return
    }
    const msgParams = new URLSearchParams({ location: location.id })
    if (configurationId) msgParams.set('config', configurationId)
    if (quantity && quantity > 1) msgParams.set('quantity', String(quantity))
    if (storageTime) msgParams.set('storageTime', storageTime)
    const messageHref = `${contact}?${msgParams.toString()}`

    if (currentUser.isVerified || currentUser.requestedVerification) {
      Router.push(messageHref)
    } else {
      const encoded = encodeURIComponent(messageHref)
      const kycParams = new URLSearchParams({
        redirectU: encoded,
        locationId: location.id
      })
      if (configurationId) kycParams.set('configurationId', configurationId)
      if (quantity) kycParams.set('quantity', String(quantity))
      if (storageTime) kycParams.set('storageTime', storageTime)
      Router.push(`${RedirectUrls.UserConfirmation}?${kycParams.toString()}`)
    }
  }

  if (isBrokerMode)
    return <a href={href} className="hidden" aria-hidden="true" />

  const verificationItems = [
    {
      Icon: PhoneIcon,
      text: isVerified
        ? t('USER.PHONE_VERIFIED')
        : t('USER.PHONE_NOT_VERIFIED'),
      verified: isVerified
    },
    {
      Icon: EmailIcon,
      text: isVerified
        ? t('USER.EMAIL_VERIFIED')
        : t('USER.EMAIL_NOT_VERIFIED'),
      verified: isVerified
    },
    {
      Icon: BadgeIcon,
      text: isVerified ? t('USER.ID_VERIFIED') : t('USER.ID_NOT_VERIFIED'),
      verified: isVerified
    }
  ]

  // Unit 15 / 2026-09-09: the ANONYMOUS host — SpotMe Now's treatment and the
  // platform's default posture. No name, no logo, no description, no link to
  // the team profile: a renter who can read the operator off the listing can
  // call that operator directly and SpotMe's recurring commission evaporates.
  // Broker/Fleet already swap in the specialist card and Traditional already
  // hides the section; this closes the last hole. Identity is disclosed at
  // checkout, where the rental contract requires it.
  //
  // 🛑 Everything here is a claim we ALREADY make elsewhere on this page. The
  // heading degrades to a plain "Anfitrión" when the host is not verified —
  // never assert verification we do not hold, and never introduce a NEW or
  // stronger verification claim here (root CLAUDE.md puts those off-limits
  // without legal review).
  if (userVariant === 'anonymous') {
    return (
      <section aria-labelledby="host-section-heading">
        <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
          Anfitrión
        </p>

        <div className="mt-6 flex items-center gap-5">
          {/* Neutral crest, NOT an avatar or a monogram — initials would leak
              the very identity this variant exists to withhold. */}
          <div
            aria-hidden="true"
            className="flex size-16 shrink-0 items-center justify-center rounded-full border border-blue-500/40 bg-cream-300 md:size-20"
          >
            <SecurityIcon
              sx={{ fontSize: 26 }}
              className="text-blue-500 md:!text-[30px]"
            />
          </div>

          <div className="min-w-0 flex-1">
            <h2
              id="host-section-heading"
              className="font-display text-[22px] leading-tight font-medium text-ink-700 md:text-[26px]"
            >
              <span className="font-display italic">
                {isVerified ? 'Anfitrión verificado' : 'Anfitrión'}
              </span>
            </h2>
            {/* No "Verificado" chip here: the heading two lines up already
                says it in 26px display type, and repeating it at 10px read as
                a template artefact in review. The four verification rows below
                carry the detail. */}
            <p className="mt-1 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              Tu reserva, tu pago y tu comunicación se gestionan dentro de
              SpotMe.
            </p>
          </div>
        </div>

        <ResponseTime show={location.respondsWithin24h === true} />

        <VerificationGrid items={verificationItems} />

        {/* One CTA only — the named branch's "Ver perfil" is exactly the link
            this variant removes. The chat itself stays: it is the renter's
            pre-booking question channel, and it runs inside SpotMe. */}
        <button
          type="button"
          onClick={handleContactHost}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 border border-ink-700 px-6 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500 md:w-auto"
        >
          <ChatOutlinedIcon className="!text-[14px]" />
          Enviar mensaje
        </button>

        <PaymentProtectionNote />
      </section>
    )
  }

  return (
    <section>
      <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
        Anfitrión
      </p>
      <h2 className="sr-only">
        {/* Single template literal — adjacent text + {name} emitted a `<!-- -->`
            boundary comment at SSR that comment-stripping intermediaries drop,
            firing #418 (Trigger H — hydration_diff 2026-07-02, iOS Safari). */}
        {`Alojado por ${name}`}
        <a href={href} className="hidden" />
      </h2>

      {/* Host masthead — avatar + name. The parent already renders a
          `<SectionDivider />` above this section, so adding another hairline
          here would read as a double break between the Header and the
          Anfitrión block. Keep the spacing, drop the extra rule. */}
      <div className="mt-6">
        <Link href={hrefWithLocation ?? href} passHref>
          <div className="flex cursor-pointer items-center gap-5">
            {/* Background-image div, NOT <Image fill> — next/image injects
                `style="…color:transparent"` at SSR that drifts on hydration
                (#418 Trigger D, hydration_diff 2026-07-08). role+aria-label
                keep a11y; the host name renders below so the photo is
                decorative. url() double-quoted so SSR matches the browser's
                CSSOM re-serialization (Trigger D Phase 6). */}
            <div
              role="img"
              aria-label={`Foto de perfil de ${name}`}
              className="size-16 shrink-0 rounded-full border border-ink-100/20 bg-grey-200 bg-cover bg-center bg-no-repeat md:size-20"
              style={{
                backgroundImage: `url("${pp?.url ?? '/images/avatar.png'}")`
              }}
            />

            <div className="min-w-0 flex-1">
              <h3 className="truncate font-display text-[22px] leading-tight font-medium text-ink-700 md:text-[26px]">
                <span className="font-display italic">{name}</span>
              </h3>
              {/* Render ONLY when `createdOn` is actually present. The public
                  `Location.user` resolver runs the host through
                  `hideConfidentialUserInfo`, whose allowlist does NOT include
                  `createdOn` — so for every anonymous visitor this value is
                  `undefined`, and `formatTimestampMonthDay` falls back to
                  `new Date()`. That rendered "Anfitrión desde <mes actual>"
                  (a false tenure claim on every listing) AND drifted
                  SSR-vs-CSR whenever ISR-cached HTML generated in one month
                  hydrated in the next → React #418 `args[]=text`
                  (fired 2026-08-01 on /pension-estacionamiento/...).
                  Restoring the real date requires adding `createdOn` to that
                  server allowlist — do NOT "fix" this by removing the guard. */}
              {createdOn ? (
                <p className="mt-1 font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
                  {`Anfitrión desde ${formatTimestampMonthDay(createdOn, {
                    year: 'numeric',
                    month: 'long',
                    day: undefined
                  })}`}
                </p>
              ) : null}
              {isVerified && (
                <div className="mt-2 inline-flex items-center gap-1.5">
                  <VerifiedCheck />
                  <span className="font-display text-[10px] tracking-[0.16em] text-blue-500 uppercase md:text-[11px]">
                    Verificado
                  </span>
                </div>
              )}
            </div>
          </div>
        </Link>

        {/* Description — editorial pull quote rhythm */}
        {desc && (
          <p className="mt-6 max-w-xl text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
            {desc}
          </p>
        )}

        <ResponseTime show={location.respondsWithin24h === true} />

        <VerificationGrid items={verificationItems} />

        {/* CTAs — editorial outline pair */}
        <div className="mt-8 flex flex-col gap-3 md:flex-row">
          <Link
            href={hrefWithLocation ?? href}
            className="inline-flex flex-1 items-center justify-center border border-ink-700 px-6 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500"
          >
            Ver perfil
          </Link>
          <button
            type="button"
            onClick={handleContactHost}
            className="inline-flex flex-1 items-center justify-center gap-2 border border-ink-100/40 px-6 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500"
          >
            <ChatOutlinedIcon className="!text-[14px]" />
            Enviar mensaje
          </button>
        </div>
      </div>

      <PaymentProtectionNote />
    </section>
  )
}

export default React.memo(User)
